import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getRandomLocation } from "../utils/mapUtils";
import adminApiClient, { MapBounds } from "../../../api/admin";

const RANDOM_OFFSET_RADIUS = 2000;
const STALE_TIME = 5 * 60 * 1000;
const CACHE_TIME = 10 * 60 * 1000;

export function useMapCollections(bounds: MapBounds) {
  const boundsKey = bounds
    ? `${bounds.northEast.lat.toFixed(4)},${bounds.northEast.lng.toFixed(4)},${bounds.southWest.lat.toFixed(4)},${bounds.southWest.lng.toFixed(4)}`
    : null;

  const {
    data: collections,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["collections-by-bounds", boundsKey],
    queryFn: async () => {
      const response = await adminApiClient.getMapCollections(bounds);
      return response;
    },
    enabled: !!bounds,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const collectionsWithRandomLocation = useMemo(() => {
    if (!collections || !Array.isArray(collections)) return [];

    return collections
      .filter(
        (collection) =>
          collection.location && collection.id && collection.coverUrl,
      )
      .map((collection) => ({
        ...collection,
        randomLocation: getRandomLocation(
          collection.location.latitude,
          collection.location.longitude,
          RANDOM_OFFSET_RADIUS,
        ),
      }));
  }, [collections]);

  return {
    collections: collectionsWithRandomLocation,
    isLoading,
    isFetching,
    error: error as Error | null,
  };
}
